import * as XLSX from "xlsx";
import { getOfficialUnitName } from "../../purchasing/utils/unitNames";

export const MAX_IMPORT_ROWS = 5000;
export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
export const PRODUCT_COLUMNS = [
  ["tender_code", "Tender code", 24, "Required. Copy the code of an existing tender from Tenders. Create tenders manually in Pilcore before importing prices."],
  ["item_code", "Item code", 22, "Required. Copy an existing corporate item code from Items. It must be linked to the tender's supplier."],
  ["unit_price", "Unit price (EUR)", 22, "Required. Non-negative price per main purchase unit shown in Items, up to 8 decimals. Do not enter thousands separators."],
  ["starts_on", "Start date", 18, "Required YYYY-MM-DD or Excel date. First day this price applies, inclusive."],
  ["ends_on", "End date", 18, "Optional YYYY-MM-DD or Excel date. Last day this price applies, inclusive. Blank means no end date."],
];

function sheet(rows, widths) {
  const result = XLSX.utils.aoa_to_sheet(rows);
  result["!cols"] = widths.map((wch) => ({ wch }));
  if (rows.length > 1) result["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length - 1, c: widths.length - 1 } }) };
  return result;
}

export function createTenderTemplate(catalog = [], measurementUnits = [], agreements = [], languageId = "en") {
  const workbook = XLSX.utils.book_new();
  workbook.Props = { Title: "Pilcore tender price import", Subject: "Prices for existing tenders and items", Author: "Pilcore" };
  const products = sheet([PRODUCT_COLUMNS.map((column) => column[1])], PRODUCT_COLUMNS.map((column) => column[2]));
  const formats = { unit_price: "0.00######", starts_on: "dd/mm/yyyy", ends_on: "dd/mm/yyyy" };
  for (let row = 1; row <= MAX_IMPORT_ROWS; row += 1) PRODUCT_COLUMNS.forEach(([field], col) => {
    // Empty string cells preserve the input format when exporting the blank template.
    products[XLSX.utils.encode_cell({ r: row, c: col })] = { t: "s", v: "", z: formats[field] || "@" };
  });
  products["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: MAX_IMPORT_ROWS, c: PRODUCT_COLUMNS.length - 1 } });
  XLSX.utils.book_append_sheet(workbook, products, "Products");
  const active = catalog.filter((entry) => entry.is_active !== false);
  const byId = new Map(catalog.map((entry) => [Number(entry.id), entry]));
  const unitById = new Map(measurementUnits.map((unit) => [Number(unit.id), unit]));
  const suppliers = active.filter((entry) => entry.kind === "supplier" && entry.data?.is_stock_supplier !== false);
  const supplierById = new Map(suppliers.map((entry) => [Number(entry.id), entry]));
  const available = agreements.filter((entry) => entry.is_active !== false && entry.can_edit !== false && (entry.currency || "EUR") === "EUR" && !String(entry.id).startsWith("legacy-"));
  XLSX.utils.book_append_sheet(workbook, sheet([
    ["Tender code", "Tender name", "Corporate supplier code", "Supplier name", "Currency", "Start date", "End date"],
    ...available.map((entry) => {
      const supplier = entry.catalog_supplier || supplierById.get(Number(entry.catalog_supplier_id));
      return [String(entry.code || entry.id), entry.name, supplier?.code || "", supplier?.name || "", entry.currency || "EUR", entry.starts_on || "", entry.ends_on || ""];
    }),
  ], [24, 42, 28, 45, 14, 18, 18]), "Tenders");
  XLSX.utils.book_append_sheet(workbook, sheet([
    ["Item code", "Item name", "Unit code", "Price per unit", "Linked supplier codes", "Linked suppliers"],
    ...active.filter((entry) => entry.kind === "item").map((entry) => {
      const unit = byId.get(Number(entry.data?.unit_id)) || unitById.get(Number(entry.data?.unit_id));
      const linked = (entry.data?.supplier_ids || []).map((id) => supplierById.get(Number(id))).filter(Boolean);
      return [entry.code, entry.name, unit?.code || "", getOfficialUnitName(unit, languageId), linked.map((supplier) => supplier.code).join("; "), linked.map((supplier) => supplier.name).join("; ")];
    }),
  ], [22, 48, 18, 24, 35, 60]), "Items");
  XLSX.utils.book_append_sheet(workbook, sheet([
    ["Corporate supplier code", "Supplier name"], ...suppliers.map((entry) => [entry.code, entry.name]),
  ], [28, 50]), "Suppliers");
  XLSX.utils.book_append_sheet(workbook, sheet([
    ["TENDER PRICE IMPORT", "Instructions"],
    ["1. Existing records only", "Create the tender, supplier and items manually in Pilcore first. This workbook only loads prices; it never creates tenders, items or suppliers."],
    ["2. Fill Products", "One item and price period per row, up to 5,000 rows. Only fill Tender code, Item code, Unit price (EUR), Start date and optional End date."],
    ["3. Reference sheets", "Tenders lists existing editable EUR tenders. Items shows each item's main purchase unit and linked suppliers. Suppliers identifies corporate supplier codes. These sheets are reference snapshots only: edits to them are not imported."],
    ["Legacy tenders", "Open and save a legacy tender manually in Pilcore before importing its prices, then download a fresh template."],
    ["Price unit", "The price is always per the item's main purchase unit shown in Items, not per an inferred box, pack or subunit. Item names, units, conversions, descriptions and purchasing conditions are retained from the catalogue."],
    ["Supplier and hotels", "The supplier and assigned hotels come from the existing tender. The item must already be linked to that supplier. This import does not change hotel assignments."],
    ["Validity", "Dates are inclusive. Start date is required; blank End date means no end date. Date columns are preformatted as DD/MM/YYYY. Enter Excel dates without a time or YYYY-MM-DD."],
    ["Overlapping prices", "Review existing prices and the resulting periods before saving. Replacing an overlap requires your explicit confirmation. Prices outside the imported dates are retained."],
    ["Example of replacement", "Existing 2026-10-12 to 2026-12-31; new 2026-11-11 to 2026-12-21: retain the old price through 2026-11-10, use the new price through 2026-12-21, then retain the old price from 2026-12-22 to 2026-12-31."],
    ["Input format", "Keep the Products headings. Keep codes as text to preserve leading zeros. The price column is preformatted as a number with two to eight decimal places. Enter prices using your Excel decimal separator, without currency symbols or thousands separators. Do not use formulas or merged cells in Products."],
    ["Validation", "Nothing is saved during review. Unknown or inactive records, supplier mismatches and invalid rows must be corrected before any prices are saved. The import is checked again on confirmation."],
    [], ["Products column", "How to fill it in"], ...PRODUCT_COLUMNS.map((column) => [column[1], column[3]]),
  ], [30, 120]), "Instructions");
  return workbook;
}

export function downloadTenderTemplate(catalog, units, agreements, languageId) {
  XLSX.writeFile(createTenderTemplate(catalog, units, agreements, languageId), "pilcore-tender-prices-template.xlsx");
}

function dateCell(cell, date1904) {
  if (!cell || cell.v === "" || cell.v == null) return "";
  let value;
  if (typeof cell.v === "number") {
    const date = XLSX.SSF.parse_date_code(cell.v, { date1904 });
    if (!date || date.H || date.M || date.S || date.u) throw new Error("Use an Excel date without a time, or YYYY-MM-DD.");
    value = `${String(date.y).padStart(4, "0")}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}`;
  } else value = String(cell.v).trim();
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(Date.parse(value)) || new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) !== value) {
    throw new Error("Use a valid date in YYYY-MM-DD format or an Excel date without a time.");
  }
  return value;
}

export function parseTenderWorkbook(data) {
  const workbook = XLSX.read(data, { type: "array", cellDates: false, cellFormula: true, sheetRows: MAX_IMPORT_ROWS + 2, sheets: ["Products"] });
  const source = workbook.Sheets.Products;
  if (!source?.["!ref"]) throw new Error('Use the downloaded template with its "Products" sheet.');
  if (source["!merges"]?.length) throw new Error("Remove merged cells from the Products sheet.");
  const range = XLSX.utils.decode_range(source["!fullref"] || source["!ref"]);
  if (range.e.r > MAX_IMPORT_ROWS) throw new Error("The Products sheet must contain at most 5,000 rows below the header. Remove unused rows.");
  const columns = new Map();
  PRODUCT_COLUMNS.forEach(([field, label]) => {
    let index = -1;
    for (let col = 0; col <= range.e.c; col += 1) {
      const cell = source[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (cell?.f || cell?.t === "e") throw new Error("Remove formulas and Excel errors from the Products header.");
      if (String(cell?.v || "").trim() === label) {
        if (index !== -1) throw new Error(`Duplicate column: ${label} in Products.`);
        index = col;
      }
    }
    if (index === -1) throw new Error(`Missing column: ${label} in Products. Download the current price template.`);
    columns.set(field, index);
  });
  const rows = [], errors = [];
  for (let row = 1; row <= range.e.r; row += 1) {
    const result = { row: row + 1 };
    let populated = false;
    for (const [field, column] of columns) {
      const cell = source[XLSX.utils.encode_cell({ r: row, c: column })];
      if (cell?.f) { errors.push({ sheet: "Products", row: row + 1, message: "Replace formulas with their values before uploading." }); populated = true; continue; }
      if (cell?.t === "e") { errors.push({ sheet: "Products", row: row + 1, message: "Correct the Excel error in this row." }); populated = true; continue; }
      try {
        result[field] = field === "starts_on" || field === "ends_on" ? dateCell(cell, workbook.Workbook?.WBProps?.date1904) : cell?.v ?? "";
        if (field.endsWith("code") && typeof cell?.v === "number") result[field] = cell.w || String(cell.v);
        if (field === "unit_price" && typeof cell?.v === "number" && Number.isFinite(cell.v) && Number(cell.v.toFixed(8)) === cell.v) result[field] = cell.v.toFixed(8);
        if (String(result[field]).trim()) populated = true;
      } catch (failure) { errors.push({ sheet: "Products", row: row + 1, message: failure.message }); populated = true; }
    }
    if (populated) rows.push(result);
  }
  if (!rows.length) throw new Error("The Products sheet has no products. Fill in at least one price row.");
  const invalidRows = new Set(errors.map((error) => error.row));
  for (const entry of rows) {
    if (invalidRows.has(entry.row)) continue;
    for (const [field, label, limit] of [["tender_code", "Tender code", 100], ["item_code", "Item code", 50], ["starts_on", "Start date", 10]]) {
      const value = String(entry[field] ?? "").trim();
      if (!value || value.length > limit) errors.push({ sheet: "Products", row: entry.row, message: `${label} is required and must not exceed ${limit} characters.` });
    }
    if (!/^\d{1,12}([.,]\d{1,8})?$/.test(String(entry.unit_price ?? "").trim())) errors.push({ sheet: "Products", row: entry.row, message: "Unit price is required. Use a non-negative number with up to eight decimals and no thousands separators." });
    if (entry.starts_on && entry.ends_on && entry.ends_on < entry.starts_on) errors.push({ sheet: "Products", row: entry.row, message: "The end date cannot precede the start date." });
  }
  return { rows, errors };
}

export function downloadImportErrors(errors) {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet([["Sheet", "Excel row", "Error"], ...errors.map((entry) => [entry.sheet || "Products", entry.row, entry.message])], [20, 16, 110]), "Errors");
  XLSX.writeFile(workbook, "pilcore-tender-import-errors.xlsx");
}
